import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import { getNumberLabel } from "../../../../../utils/numberUtils";
import { HgNaics } from "../../../../../types/taxonomy";
import { useTaxonomyContext } from "../../../../../contexts/Taxonomy/TaxonomyContext";
import { format, setMonth } from "date-fns";
import { useDUNSVisibility } from "../../../../../hooks/useDUNSVisibility";
import { CompanyDunsDetails } from "./CompanyDunsDetails";
import { displayDiscreteValue } from "../../../utils";

interface DetailItemProps {
  label: string;
  children: React.ReactNode;
  isMonospace?: boolean;
}

export const DetailItem: React.FC<DetailItemProps> = React.memo(
  ({ label, children, isMonospace = false }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          whiteSpace: "pre-line",
          wordBreak: "break-word",
          ...(isMonospace && { fontFamily: "monospace" }),
        }}
      >
        {children}
      </Typography>
    </Box>
  )
);

DetailItem.displayName = "DetailItem";

const getFormattedUrl = (url: string) => {
  if (url && !url.match(/^https?:\/\//)) return `https://${url}`;

  return url;
};

const getMonthName = (monthNumber: number): string => {
  if (monthNumber < 1 || monthNumber > 12) return "";

  const dateWithMonth = setMonth(new Date(0), monthNumber - 1);
  return format(dateWithMonth, "MMMM");
};

const buildNaicsBreadcrumb = (
  naicsData: HgNaics[],
  companyNaics?: HgNaics
): string => {
  if (!companyNaics) return "-";

  let breadcrumb = companyNaics.name;
  let currentParentId = companyNaics.parentId;

  while (currentParentId) {
    const parentNode = naicsData.find((naics) => naics.id === currentParentId);

    if (parentNode) {
      breadcrumb = `${parentNode.name} > ${breadcrumb}`;
      currentParentId = parentNode.parentId;
    } else {
      break;
    }
  }

  return breadcrumb;
};

export const CompanyDetails: React.FC = React.memo(() => {
  const company = useCompanyPageStore((state) => state.company);
  const { naics } = useTaxonomyContext();
  const isDUNSVisible = useDUNSVisibility();

  const naicsBreadcrumb = useMemo(
    () => buildNaicsBreadcrumb(naics, company?.naics_2012),
    [naics, company?.naics_2012]
  );

  const fullAddress = useMemo(() => {
    if (!company?.hgId) return "";

    const cityState = [
      company.locationPrimary?.city,
      company.locationPrimary?.state?.name,
    ]
      .filter(Boolean)
      .join(", ");

    const addressParts = [
      company.address?.lineOne,
      cityState,
      company.address?.postalCode,
      company.locationPrimary?.state?.country?.alpha2,
    ];
    return addressParts.filter(Boolean).join("\n");
  }, [company?.hgId, company?.locationPrimary, company?.address]);

  const urlDisplay = useMemo(() => {
    if (!company?.url) return null;
    return (
      <Link
        href={getFormattedUrl(company.url)}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        sx={{
          textDecoration: "none",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        }}
      >
        {company.url}
      </Link>
    );
  }, [company?.url]);

  const revenueDisplayValue = useMemo(() => {
    if (!company?.revenueRange) return undefined;
    return displayDiscreteValue(company.revenueRange)
      ? getNumberLabel(company.revenueRange?.min || 0)
      : company.revenueRange?.band;
  }, [company?.revenueRange]);

  const employeeDisplayValue = useMemo(() => {
    if (!company?.employeeRange) return undefined;
    return displayDiscreteValue(company.employeeRange)
      ? getNumberLabel(company.employeeRange?.min || 0)
      : company.employeeRange?.band;
  }, [company?.employeeRange]);

  if (!company) return null;

  return (
    <Box data-testid="company-detail-summary-details-section">
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Company Details
      </Typography>

      <Grid container spacing={3}>
        {/* First Column */}
        <Grid size={4}>
          <DetailItem label="Address">{fullAddress}</DetailItem>
          {company.telephone && company.telephoneCountryCode && (
            <DetailItem label="Phone">{`+${company.telephoneCountryCode} ${company.telephone}`}</DetailItem>
          )}
          <DetailItem label="URL">{urlDisplay}</DetailItem>
          {company.ownershipType && (
            <DetailItem label="Ownership">{company.ownershipType}</DetailItem>
          )}
        </Grid>

        {/* Second Column */}
        <Grid size={4}>
          <DetailItem label="Employees">{employeeDisplayValue}</DetailItem>
          <DetailItem label="HG ID" isMonospace>
            {company.hgId}
          </DetailItem>
          {isDUNSVisible && (
            <CompanyDunsDetails>
              <DetailItem label="DUNS">{company.duns || "-"}</DetailItem>
            </CompanyDunsDetails>
          )}
          <DetailItem label="Industry (NAICS)">
            <Box component="span">
              <Typography variant="body2" component="span">
                {company.naics_2012?.code
                  ? `${company.naics_2012?.code}: `
                  : ""}
              </Typography>
              {naicsBreadcrumb}
            </Box>
          </DetailItem>
        </Grid>

        {/* Third Column */}
        <Grid size={4}>
          <DetailItem label="Global Presence">
            {company.isMultiNational ? "Multi-National" : "Domestic"}
          </DetailItem>
          <DetailItem label="Industry (HG)">
            {company.industry?.name || ""}
          </DetailItem>
          <DetailItem label="Revenue">{revenueDisplayValue}</DetailItem>
          {!!company.fiscalYearEndMonth && (
            <DetailItem label="Financial Year End">
              {getMonthName(company.fiscalYearEndMonth)}
            </DetailItem>
          )}
        </Grid>
      </Grid>
    </Box>
  );
});

CompanyDetails.displayName = "CompanyDetails";
